import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

export interface CrystalRollResult {
  kind: "named" | "universal";
  rarity: number;       // 0..3
  amount: number;
  heroId?: string | null;
  heroName?: string | null;
}

export interface OpenCrystalResponse {
  drop: CrystalRollResult;
  newState: unknown;
}

// Opens one crystal of the given tier (1=Dim, 2=Shining, 3=Ancient).
// Backend deducts the crystal, rolls the weighted-random drop table from
// HeroCrystalCatalog, and credits the result to the player's shards.
export function useOpenCrystal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (tier: number): Promise<OpenCrystalResponse> => {
      const res = await api.post<OpenCrystalResponse>(`/crystals/open`, { tier });
      return res.data;
    },
    onSuccess: () => {
      // Castle has new shard / crystal totals, heroes screen needs refresh.
      qc.invalidateQueries({ queryKey: ["castle"] });
      qc.invalidateQueries({ queryKey: ["heroes"] });
    },
  });
}

// Converts N universal shards of `fromRarity` into N named shards of `toHeroId`.
// Hero must be of the same rarity (server-enforced). Used to top up a hero
// who's a few shards short of recruit.
export function useConvertShards() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      fromRarity: number;
      toHeroId: string;
      amount: number;
    }) => {
      const res = await api.post(`/crystals/convert`, params);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["castle"] });
      qc.invalidateQueries({ queryKey: ["heroes"] });
    },
  });
}
